import React, {Component} from 'react';
import CSSModules from 'react-css-modules'
import config from 'config'
import container from '../../helpers/container'
import {
  ContainerDivs,
  HomeEduCenter,
  RiskTypeLinks,
  // SolutionsList,
  Link,
  LinkIcon,
} from '../../components'
import Styles from './FossilFuelBondRisks.css'


@CSSModules(Styles, {allowMultiple:true})
export default class FossilFuelBondRisks extends Component {
  constructor(props) {
    super(props)
    this.state = {
      title: 'Financial risks of fossil fuel bonds',
      description: 'Fossil fuel stocks and bonds pose different risks to the climate, and face different financial risks.',
      url: config.domain + config.routes.eduCenterPlanAdmins
    }
  }

  render() {
    // const img1 = require( '../../../static/images/millenial_employees.jpg' )
    //
    return (
      <div styleName='page'>
        {
          container.helmet(
            this.state.title,
            this.state.description,
            this.state.url
          )
        }
        <ContainerDivs>
          <div styleName='small-page-title'>
            {this.state.title}
          </div>
          <div styleName='copy'>

            <h3>
              Fossil fuel stocks and bonds pose different risks to the climate, and face different financial risks.
            </h3>

            <p>
              Climate-related financial risks are spread throughout the global economy. Governments, banks, businesses, developers, and investors face different risks as the planet warms. For 401(k) investors, most of the focus to date has been on the risks of investing in the stocks of high-carbon companies. To address this risk, more employers are adding sustainable stock funds to their retirement plan lineup. But there's a problem: retirement plans are still investing billions in fossil fuel bonds, which carry their own kind of climate risk.
            </p>

            <blockquote styleName='blockquote'>
              <div styleName='blockquote-quotes'>
                &#8220;
              </div>
              <div>
              <p styleName='blockquote-text'>
                Instead of slowing down the decarbonization of the global economy, now is the time to accelerate the energy transition to a renewable energy future. Fossil fuels are a dead end – for our planet, for humanity, and yes, for economies.
              </p>
              <cite styleName='blockquote-cite'>
                United Nations Secretary-General António Guterres
              </cite>
              </div>
            </blockquote>

            <h2>
              Stocks and bonds
            </h2>

            <p>
              First, a primer:
            </p>

            <p>
              Buying <b>stocks</b> (also called <b>equity</b>) means you own a fraction of a corporation, giving you a (small) voice in how the company is run. Most people are more concerned with the value of their shares rising over time, meaning they want to invest in companies that they think will succeed in the future.
            </p>

            <p>
              On the other hand, buying <b>bonds</b> (also called <b>debt</b> or <b>fixed income</b>) means you’re lending money to some company or organization. Most bonds are issued by governments, mortgage-issuers, banks, and businesses. For example, companies issue bonds to pay for expansions to operations. Bonds pay you a steady stream over time, and eventually you get paid back in full for your initial investment as well.
            </p>

            <p>
              Bond prices are less volatile than stock prices and are preferred by investors looking for safe harbors. This includes major financial institutions and large pension fund managers, but it can also include 401(k) investors, especially those closer in age to retirement.
            </p>

            <h2>
              Risk to climate, risk to portfolio
            </h2>

            <p>
              What does this have to do with the climate?
            </p>

            <p>
              Climate risk can be thought of in two ways: the risk that the climate will hurt your investments, and the risk that your investments will hurt the climate. These risks are different for stocks and bonds.
            </p>

            <p>
              For 401(k) investors, the main financial risk of investing in high-carbon industries like fossil fuel energy and utilities are that those stocks might be worth less in the future. There's <b>physical risk</b>: impacts of the climate crisis might hurt their operations - think hurricanes hitting offshore drilling rigs and shutting down coastal refineries. And there's also <b>transition risk</b>: the clean energy transition might make their business models uncompetitive, or governments might regulate them, ordering them to reduce emissions, impacting profitability.
            </p>

            <p>
              Every sector in a diversified portfolio faces risk of losses from rising temperatures, but high-carbon industries are acutely at risk, particularly from transition risks. The climate crisis is driven by emissions, and governments agree that emissions must be lowered. High-carbon business models are what we are transitioning away from. These industry-wide pressures are what long-term investors point to as many have pulled back from coal and oil & gas over the past decade.
            </p>

            <p>
              The connection between stocks and emissions is diffuse. Critics of fossil free investing say most stocks are bought from other investors, not the company directly. If you sell your shares, there's little or no impact on the company, and now someone else is the "owner" of that company, maybe someone who cares less about climate change than you do. It's true that companies don't really pay attention to whether any given 401(k) investor owns their stock or not - but they do pay attention when there's downward pressure on their stock price, and when major institutions like banks, pension funds, and university endowments stop investing. The main impact on emissions of fossil free stock investing is the network effect of signaling to markets and governments that we must accelerate the net zero transition.
            </p>

            <h2>
              Fossil fuel bonds
            </h2>

            <p>
              How are fossil fuel bonds different from fossil fuel stocks in terms of climate risk? If bonds are generally safer than stocks, how does climate change impact that?
            </p>

            <p>
              Because they're considered lower risk, bonds tend to have lower returns than stocks - which is why between 60-80% of the money in any given 401(k) plan is invested in stocks. This partially explains why most of the focus of sustainable investing has been on stocks.
            </p>

            <p>
              It's possible a bond might not be paid off if the company goes out of business. But generally, all things being equal, a fossil fuel bond is a safer investment than a fossil fuel stock. Because of that, and because the average 401(k) plan has 10 times more invested in fossil fuel stocks than fossil fuel bonds, an individual's investment risk may be higher with fossil fuel stocks.
            </p>

            <p>
              The flip side of this is that since bond prices are less volatile and returns more similar across issuers, it reduces the friction to replacing fossil fuel bonds from similar bonds from other institutions.
            </p>

            <p>
              While risk to portfolio might be lower, risk to climate is not. If the impact on the climate from a stock is diffuse, the impact from a bond is direct. Bond issuances are one of the main ways fossil fuel companies raise capital to expand operations. The International Energy Agency has warned that there can be no new coal, oil, and gas development if humanity wants to prevent dangerous warming. If there are fossil fuel bonds in your 401(k), you're effectively lending your retirement savings to companies so they can mine for coal and drill for oil.
            </p>

            <p>
              In this way, you're funding operations that are generating higher emissions and thus contributing to the higher risks faced by the entire portfolio from climate change.
            </p>

            <h2>
              Reducing climate risk
            </h2>

            <p>
              What does this mean for retirement plans? If a company fails to properly manage climate risk in its retirement plan, it could constitute a failure of its fiduciary duty to manage the plan in the best interest of plan participants and beneficiaries. As New York Comptroller Thomas DiNapoli explained when announcing the state’s plan to enforce a carbon-neutrality mandate, “investing for the low-carbon future is essential to protect the fund’s long-term value.” At least one employer has already faced lawsuits from an employee who claimed his pension fund did not adequately disclose or assess the effect of climate change on its investments.
            </p>

            <p>
              The best way for retirement plans to protect their employees from climate risk is to make sure they have access to sustainable investments, and then to make sure the default investment option is considering climate risk factors. They should also engage the asset managers responsible for the fund portfolios. If asset managers aren't providing appropriate bond fund options that reduce exposure to climate-risky fossil fuel corporate bonds, companies should let them know that their employees deserve climate-safe investments.
            </p>

            <h2>
              <Link to={config.routes.fossilFuelBondsIn401ks}
                    title='Fossil Fuel Bonds in 401(k)s'
                    xstrong
              >
                See more on fossil fuel bonds in 401(k)s<LinkIcon />
              </Link>
            </h2>

          </div>
        </ContainerDivs>
        <RiskTypeLinks/>
        <HomeEduCenter/>
      </div>
    )
  }
}
